import { useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import { fetchStoredDocuments, type StoredDocument } from '@/lib/api'
import {
  ATTACH_ACTION,
  CHANGE_ACTION,
  REMOVE_ACTION,
  CHOOSE_PROMPT,
  NO_FILES,
  attachmentLine,
  reachesFewerLine,
  fileOptionLabel,
  setLiveFactAttachment,
  type FactAttachment,
} from '@/lib/fact-files'

/**
 * THE FILE AN ANSWER OFFERS (G46), on a Facts card.
 *
 * Shown only for an answer the owner wrote on a pod that keeps files (the
 * engine sends `attachment` only then), so Foredesk's card and a document
 * fact's card are unchanged. Every word comes from src/lib/fact-files.ts.
 *
 * The chooser lists the kept files fresh each time it opens — a list held
 * from page load could offer a file deleted since, and the engine would say
 * so; this way it rarely has to. After any change the whole list reloads
 * (onChanged), so the card shows what the engine now holds, not what was
 * clicked.
 *
 * NAMED GAP, as tests/kelvo-dashboard.test.ts names it: there is no component
 * harness (no jsdom), so nothing renders this panel. Its words, its request
 * and the engine behind it are pinned; that the chooser lists files fresh
 * when opened is pinned only by reading the source, and proven by the live
 * walk (G46: change the file after deleting another).
 */
export function FactFilePanel({
  factId,
  attachment,
  onChanged,
}: {
  factId: string
  attachment: FactAttachment | null
  onChanged: () => void
}) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [choosing, setChoosing] = useState(false)
  const [files, setFiles] = useState<StoredDocument[] | null>(null)
  const [choice, setChoice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function open() {
    setError(null)
    setChoosing(true)
    setFiles(null)
    setChoice(attachment?.id ?? '')
    void (async () => {
      try {
        setFiles(await fetchStoredDocuments(await getTokenRef.current()))
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your kept files. Try again.')
        setChoosing(false)
      }
    })()
  }

  function save(documentId: string | null) {
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        await setLiveFactAttachment(await getTokenRef.current(), factId, documentId)
        setChoosing(false)
        onChanged()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That did not save. Try again.')
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <div className="mt-1">
      {attachment ? (
        <>
          <p className="text-xs text-text-muted">{attachmentLine(attachment)}</p>
          {attachment.reachesFewer ? (
            <p className="mt-1 text-xs font-medium text-text-danger">{reachesFewerLine(attachment)}</p>
          ) : null}
        </>
      ) : null}

      {choosing ? (
        <div className="mt-2 rounded-lg border border-border-subtle bg-surface-base p-3">
          {files === null ? (
            <p className="text-sm text-text-muted">Loading your kept files…</p>
          ) : files.length === 0 ? (
            <p className="text-sm text-text-muted">{NO_FILES}</p>
          ) : (
            <>
              <label htmlFor={`file-${factId}`} className="text-sm text-text-primary">
                {CHOOSE_PROMPT}
              </label>
              <select
                id={`file-${factId}`}
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                disabled={busy}
                className="mt-1.5 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus:border-brand-accent focus:outline-hidden"
              >
                <option value="">Choose a file…</option>
                {files.map((f) => (
                  <option key={f.id} value={f.id}>
                    {fileOptionLabel(f)}
                  </option>
                ))}
              </select>
            </>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {files !== null && files.length > 0 ? (
              <button
                onClick={() => save(choice)}
                disabled={busy || choice === '' || choice === (attachment?.id ?? '')}
                aria-busy={busy}
                className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
              >
                {busy ? 'Saving…' : 'Use this file'}
              </button>
            ) : null}
            <button
              onClick={() => {
                setChoosing(false)
                setError(null)
              }}
              disabled={busy}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-raised disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap items-center gap-4">
          <button onClick={open} disabled={busy} className="text-xs text-text-primary underline underline-offset-2">
            {attachment ? CHANGE_ACTION : ATTACH_ACTION}
          </button>
          {attachment ? (
            <button
              onClick={() => save(null)}
              disabled={busy}
              className="text-xs text-text-muted underline underline-offset-2"
            >
              {busy ? 'Taking it off…' : REMOVE_ACTION}
            </button>
          ) : null}
        </div>
      )}

      {error ? (
        <p role="alert" className="mt-1 text-sm text-text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
